/**
 * Email address validation.
 *
 * Ported to TypeScript from the `validator` package (MIT licensed). The
 * patterns below follow RFC 2822 (local part / display name) and delegate
 * domain validation to {@link isFQDN}, {@link isIP}, and {@link isByteLength}.
 *
 * @module isEmail
 */

import isByteLength from "./isByteLength.ts";
import isFQDN, { type IsFQDNOptions } from "./isFQDN.ts";
import isIP, { type IsIPOptions } from "./isIP.ts";

// Patterns for the local part and display name. The control-character ranges
// below are part of the RFC 2822 quoted-string / display-name grammar, hence
// the `no-control-regex` exemptions. See the RFC link in `validateDisplayName`.
/* eslint-disable no-control-regex */
const SPLIT_NAME_ADDRESS = /^([^\x00-\x1F\x7F-\x9F\cX]+)</i;
const EMAIL_USER_PART = /^[a-z\d!#$%&'*+\-/=?^_`{|}~]+$/i;

const QUOTED_EMAIL_USER =
  /^([\s\x01-\x08\x0b\x0c\x0e-\x1f\x7f\x21\x23-\x5b\x5d-\x7e]|(\\[\x01-\x09\x0b\x0c\x0d-\x7f]))*$/i;
const EMAIL_USER_UTF8_PART =
  /^[a-z\d!#$%&'*+\-/=?^_`{|}~\u00A1-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF]+$/i;

const QUOTED_EMAIL_USER_UTF8 =
  /^([\s\x01-\x08\x0b\x0c\x0e-\x1f\x7f\x21\x23-\x5b\x5d-\x7e\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF]|(\\[\x01-\x09\x0b\x0c\x0d-\x7f\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF]))*$/i;
/* eslint-enable no-control-regex */
const DEFAULT_MAX_EMAIL_LENGTH = 254;

// Display-name helpers (no control chars, so outside the block above).
/** Captures the contents of a fully double-quoted display name. */
const DISPLAY_NAME_QUOTES = /^"(.+)"$/;
/** Matches a leading `<` or trailing `>` wrapping the address. */
const DISPLAY_NAME_ANGLE_BRACKETS = /(^<|>$)/g;
/** Characters that require a display name to be double-quoted. */
const ILLEGAL_DISPLAY_NAME_CHARS = /[.";<>]/;

/**
 * Validate display name according to the RFC2822: https://tools.ietf.org/html/rfc2822#appendix-A.1.2
 */
function validateDisplayName(displayName: string) {
  const displayNameWithoutQuotes = displayName.replace(
    DISPLAY_NAME_QUOTES,
    "$1",
  );
  // display name with only spaces is not valid
  if (!displayNameWithoutQuotes.trim()) {
    return false;
  }

  // check whether display name contains illegal character
  const containsIllegal = ILLEGAL_DISPLAY_NAME_CHARS.test(
    displayNameWithoutQuotes,
  );
  if (containsIllegal) {
    // if contains illegal characters,
    // must to be enclosed in double-quotes, otherwise it's not a valid display name
    if (displayNameWithoutQuotes === displayName) {
      return false;
    }

    // the quotes in display name must start with character symbol \
    const allStartWithBackSlash =
      displayNameWithoutQuotes.split('"').length ===
      displayNameWithoutQuotes.split('\\"').length;
    if (!allStartWithBackSlash) {
      return false;
    }
  }

  return true;
}

/**
 * An entry for {@link IsEmailOptions.hostBlocklist} / `hostAllowlist`: either an
 * exact (lowercased) host string or a `RegExp` tested against the host.
 */
export type HostPattern = string | RegExp;

/** Returns `true` if `host` equals or matches any of the given patterns. */
function checkHost(host: string, patterns: readonly HostPattern[]) {
  return patterns.some(
    (pattern) =>
      host === pattern || (pattern instanceof RegExp && pattern.test(host)),
  );
}

/**
 * Tuning flags for {@link isEmail}. Inherits the domain-side options from
 * {@link IsFQDNOptions} and {@link IsIPOptions}.
 */
export interface IsEmailOptions extends IsFQDNOptions, IsIPOptions {
  /** Accept a leading display name, e.g. `Name <a@b.com>`. Defaults to `false`. */
  allowDisplayName?: boolean;
  /** Require a display name to be present. Defaults to `false`. */
  requireDisplayName?: boolean;
  /** Allow UTF-8 characters in the local part. Defaults to `true`. */
  allowUtf8LocalPart?: boolean;
  /** Reject the address if the local part contains any of these characters. */
  blocklistedChars?: string;
  /** Skip the 254-char total / 64-byte local-part length caps. Defaults to `false`. */
  ignoreMaxLength?: boolean;
  /** Reject addresses whose host matches any of these patterns. */
  hostBlocklist?: readonly HostPattern[];
  /** Accept only addresses whose host matches one of these patterns. */
  hostAllowlist?: readonly HostPattern[];
  /** Allow an IP-address domain, optionally bracketed (`a@[127.0.0.1]`). Defaults to `false`. */
  allowIpDomain?: boolean;
}

/**
 * Reports whether a string is a valid email address.
 *
 * By default the domain must be a valid FQDN and the whole address must be at
 * most 254 characters. Use {@link IsEmailOptions} to allow display names, IP
 * domains, UTF-8 local parts, or to constrain the host.
 *
 * @param str - The candidate email address.
 * @param options - See {@link IsEmailOptions}.
 * @returns `true` if the string is a valid email address under the given options.
 *
 * @example
 * ```ts
 * isEmail("user@example.com"); // true
 * isEmail("user@localhost", { requireTld: false }); // true
 * isEmail("Name <user@example.com>", { allowDisplayName: true }); // true
 * ```
 */
export default function isEmail(
  str: string,
  {
    allowDisplayName = false,
    requireDisplayName = false,
    allowUtf8LocalPart = true,
    blocklistedChars = "",
    ignoreMaxLength = false,
    hostBlocklist = [],
    hostAllowlist = [],
    allowIpDomain = false,
    ...options
  }: IsEmailOptions = {},
): boolean {
  if (requireDisplayName || allowDisplayName) {
    const displayEmail = SPLIT_NAME_ADDRESS.exec(str);
    if (displayEmail) {
      let displayName = displayEmail[1] ?? "";

      // Remove display name and angle brackets to get email address
      // Can be done in the regex but will introduce a ReDOS (See  #1597 for more info)
      str = str
        .replace(displayName, "")
        .replace(DISPLAY_NAME_ANGLE_BRACKETS, "");

      // sometimes need to trim the last space to get the display name
      // because there may be a space between display name and email address
      // eg. myname <address@gmail.com>
      // the display name is `myname` instead of `myname `, so need to trim the last space
      if (displayName.endsWith(" ")) {
        displayName = displayName.slice(0, -1);
      }

      if (!validateDisplayName(displayName)) {
        return false;
      }
    } else if (requireDisplayName) {
      return false;
    }
  }
  if (!ignoreMaxLength && str.length > DEFAULT_MAX_EMAIL_LENGTH) {
    return false;
  }

  const parts = str.split("@");
  const domain = parts.pop() ?? "";
  const lowerDomain = domain.toLowerCase();

  if (hostBlocklist.length > 0 && checkHost(lowerDomain, hostBlocklist)) {
    return false;
  }

  if (hostAllowlist.length > 0 && !checkHost(lowerDomain, hostAllowlist)) {
    return false;
  }

  let user = parts.join("@");

  if (
    !ignoreMaxLength &&
    (!isByteLength(user, { max: 64 }) || !isByteLength(domain, { max: 254 }))
  ) {
    return false;
  }

  if (
    !isFQDN(domain, {
      ...options,
      ignoreMaxLength,
    })
  ) {
    if (!allowIpDomain) {
      return false;
    }

    if (!isIP(domain, options)) {
      if (!domain.startsWith("[") || !domain.endsWith("]")) {
        return false;
      }

      const noBracketdomain = domain.slice(1, -1);

      if (noBracketdomain.length === 0 || !isIP(noBracketdomain, options)) {
        return false;
      }
    }
  }

  if (blocklistedChars) {
    if (user.search(new RegExp(`[${blocklistedChars}]+`, "g")) !== -1)
      return false;
  }

  if (user.startsWith('"') && user.endsWith('"')) {
    user = user.slice(1, user.length - 1);
    return allowUtf8LocalPart
      ? QUOTED_EMAIL_USER_UTF8.test(user)
      : QUOTED_EMAIL_USER.test(user);
  }

  const pattern = allowUtf8LocalPart ? EMAIL_USER_UTF8_PART : EMAIL_USER_PART;
  return user.split(".").every((userPart) => pattern.test(userPart));
}
